// prettier-ignore
class PredictiveSearch extends SearchForm {
	constructor() {
		super();
		this.cachedResults = {};
		this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
		this.allPredictiveSearchInstances =
			document.querySelectorAll("predictive-search");
		this.isOpen = false;
		this.abortController = new AbortController();
		this.searchTerm = "";
		this.body = document.querySelector("body");
		this.headerBottom = document.querySelector(".header__bottom");
		this.promoModal = document.querySelector(".search__modal");
		this.collectionList = document.querySelector(".template-404__collections");
		this.headerInput = document.querySelector(".header__search .search__input");
		this.setupEventListeners();
		this.initEventListeners();
	}

	setupEventListeners() {
		this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
		this.input.addEventListener("focus", this.onFocus.bind(this));
		this.addEventListener("focusout", this.onFocusOut.bind(this));
		this.addEventListener("keyup", this.onKeyup.bind(this));
	}

	getQuery() {
		return this.input.value.trim();
	}

	onChange() {
		const newSearchTerm = this.getQuery();
		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
			// Remove the results when they are no longer relevant for the new search term
			// so they don't show up when the dropdown opens again
			this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
		}

		// Update the term asap, don't wait for the predictive search query to finish loading
		this.updateSearchForTerm(this.searchTerm, newSearchTerm);

		this.searchTerm = newSearchTerm;

		if (!this.searchTerm.length) {
			this.close(true);
			return;
		}

		this.getSearchResults(this.searchTerm);
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	onFormSubmit(event) {
		if (
			!this.getQuery().length ||
			this.querySelector('[aria-selected="true"] a') ||
			event.submitter.classList.contains("card__link")
		)
			event.preventDefault();
	}

	onFormReset(event) {
		super.onFormReset(event);
		if (super.shouldResetForm()) {
			this.searchTerm = "";
			this.abortController.abort();
			this.abortController = new AbortController();
			this.closeResults(true);
		}
	}

	onFocus() {
		const currentSearchTerm = this.getQuery();
		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.add("header__bottom--visible");
			}
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.remove("hidden");
			}
		}

		if (!currentSearchTerm.length) {
			return;
		}

		if (this.searchTerm !== currentSearchTerm) {
			// Search term was changed from other search input, treat it as a user change
			this.onChange();
		} else if (this.getAttribute("results") === "true") {
			this.open();
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.add("hidden");
			}
		} else {
			this.getSearchResults(this.searchTerm);
		}
	}

	onFocusOut() {
		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.remove("header__bottom--visible");
			}
		}
		if (!this.classList.contains("search-modal__form")) {
			setTimeout(() => {
				if (!this.contains(document.activeElement)) this.close(true);
			});
		}
	}

	onBlur() {
		this.headerInput.blur();
	}

	onKeyup(event) {
		if (!this.getQuery().length) this.close(true);
		event.preventDefault();

		switch (event.code) {
			case "Enter":
				event.preventDefault();
				this.selectOption();
				break;
			case "Escape":
				this.close(true);
				this.closeResults(clearSearchTerm);
				if (this.getQuery().length) {
					this.hidePromoBlock();
				} else {
					this.showPromoBlock();
				}
				break;
		}
	}

	updateSearchForTerm(previousTerm, newTerm) {
		const searchForTextElement = this.querySelector(
			"[data-predictive-search-search-for-text]",
		);
		const currentButtonText = searchForTextElement?.innerText;
		if (currentButtonText) {
			if (
				currentButtonText.matchAll(new RegExp(previousTerm, "g")).length > 1
			) {
				// The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
				return;
			}
			const newButtonText = currentButtonText.replace(previousTerm, newTerm);
			searchForTextElement.innerText = newButtonText;
		}
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	selectOption() {
		const selectedOption = this.querySelector(
			'[aria-selected="true"] a, button[aria-selected="true"]',
		);

		if (selectedOption) selectedOption.click();
	}

	getSearchResults(searchTerm) {
		const queryKey = searchTerm.replace(" ", "-").toLowerCase();
		this.setLiveRegionLoadingState();

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey]);
			this.clickSearchTabs();
			return;
		}

		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}

		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}

		// Use the same `/search` endpoint as the search results page (NOT
		// `/search/suggest`) so the popup shows the same items in the same
		// order as the full results page.
		//
		// Two parallel fetches — one scoped to `type=article`, one scoped to
		// `type=product`. Each scoped request lets the section's count come
		// from `search.results_count` (the type total), which matches the
		// numbers shown on each tab of the /search page. A single mixed
		// fetch would only give us "items on page 1 of mixed results" —
		// nearly always smaller than the true per-type totals.
		const baseUrl = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&options%5Bprefix%5D=last&section_id=predictive-search`;
		Promise.all([
			fetch(`${baseUrl}&type=article`, { signal: this.abortController.signal }).then((r) => {
				if (!r.ok) throw new Error(r.status);
				return r.text();
			}),
			fetch(`${baseUrl}&type=product`, { signal: this.abortController.signal }).then((r) => {
				if (!r.ok) throw new Error(r.status);
				return r.text();
			}),
		])
			.then(([articlesHtml, productsHtml]) => {
				// Splice the two type-scoped responses into one popup: take the
				// Latest News section from the articles response and the Trending
				// Products section from the products response.
				const articlesDoc = new DOMParser().parseFromString(articlesHtml, "text/html");
				const productsDoc = new DOMParser().parseFromString(productsHtml, "text/html");
				const articlesSectionEl = articlesDoc.querySelector("[data-search-initial-news]");
				const productsSectionEl = productsDoc.querySelector("[data-search-initial-products]");
				// Start from the articles response (has the close button + wrapper);
				// drop any empty/no-results product section it might have rendered
				// and append the products section from the products response.
				const wrapper = articlesDoc.querySelector("#shopify-section-predictive-search");
				if (!wrapper) {
					return new DOMParser().parseFromString(articlesHtml, "text/html");
				}
				const contentRoot =
					wrapper.querySelector(".search-initial-content__content") ||
					wrapper.querySelector(".predictive-search__results-panel");
				const staleProducts = wrapper.querySelector("[data-search-initial-products]");
				if (staleProducts) staleProducts.remove();
				if (productsSectionEl && contentRoot) {
					contentRoot.appendChild(productsSectionEl);
				}
				// When at least one type has results, drop any "no results"
				// empty-state UI that the articles-type fetch may have left
				// behind. Only if BOTH fetches return empty do we keep it.
				if (articlesSectionEl || productsSectionEl) {
					const stray = wrapper.querySelector("[data-no-results]");
					if (stray) stray.remove();
				}
				return articlesDoc;
			})
			.then((doc) => {
				const text = doc.documentElement
					? "<!doctype html>" + doc.documentElement.outerHTML
					: "";
				const resultsMarkup = new DOMParser()
					.parseFromString(text, "text/html")
					.querySelector("#shopify-section-predictive-search").innerHTML;
				// Save bandwidth keeping the cache in all instances synced
				this.allPredictiveSearchInstances.forEach(
					(predictiveSearchInstance) => {
						predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
					},
				);
				this.renderSearchResults(resultsMarkup);
				this.clickSearchTabs();
				try {
					colorSwatches();
				} catch (err) {}
				(function () {
					let swiper;
				
					const initSlider = () => {
						const slider = document.querySelector(".swiper-container");
						if (!slider || slider.classList.contains("slider_started")) {
							return;
						}
						slider.classList.add("slider_started");
					let loop  
					if(slider.dataset.loop === "true") {
						loop = true
					} else if(slider.dataset.loop === "false") {
						loop = false
					}
				
				
						swiper = new Swiper(".swiper-container", {
							slidesPerView: 1,
							speed: 650,
							spaceBetween: 2,
							loop: loop,
							navigation: {
								nextEl: ".product-button-group .swiper-button-next-1",
								prevEl: ".product-button-group .swiper-button-prev-1",
							},
							pagination: {
								el: ".product-card-pagination",
								clickable: true,
							},
						});
				document.addEventListener('mouseover', (event) => {
						const buttonNext = event.target.closest('.button-next');
						if (buttonNext) {
								const swiperButton = buttonNext.querySelector('.swiper-button-next-1');
								const cursor = buttonNext.querySelector('.product__cursor');
								if (swiperButton?.classList.contains('swiper-button-disabled') && cursor) {
										cursor.style.opacity = '0.5';
								}
						}
				});
						const cursorProgress = (productSwiper) => {
							const cursor = productSwiper.querySelector(".product__cursor");
							const cursorIcon = productSwiper.querySelector("#cursor-icon1");
						
							const animateCursor = (e) => {
								const contentRect = productSwiper.getBoundingClientRect();
								const x = e.clientX - contentRect.left - 12;
								const y = e.clientY - contentRect.top - 12;
						
								cursor.animate(
									{ transform: `translate(${x}px, ${y}px)` },
									{ duration: 300, fill: "forwards" }
								);
							};
						
							const getCursorClass = (position, width) => {
								return position < width / 2 ? "prev" : "next";
							};
						
							productSwiper.addEventListener("mousemove", (e) => {
								if (!e.target.closest(".link") &&
								!e.target.closest(".swiper-pagination") ) {
									cursor.classList.add("active");
								} else {
									cursor.classList.remove("active");
								}
						
								animateCursor(e);
						
								const position = e.clientX - productSwiper.getBoundingClientRect().left;
								cursor.dataset.type = getCursorClass(position, productSwiper.clientWidth);
						
								cursorIcon.className = cursor.dataset.type; 
						
								
							});
						
							productSwiper.addEventListener("mouseleave", () => {
								cursor.classList.remove("active");
							});
						};
						
				
						const productCards = document.querySelectorAll(".card--product_cursor");
						productCards.forEach((productCard) => cursorProgress(productCard));
					};
				
					initSlider();
					document.addEventListener("shopify:section:load", initSlider);
					
				})();
			})
			
			.catch((error) => {
				if (error?.code === 20) {
					// Code 20 means the call was aborted
					return;
				}
				this.close();
				throw error;
			});

		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	setLiveRegionLoadingState() {
		this.statusElement =
			this.statusElement || this.querySelector(".predictive-search-status");
		this.loadingText =
			this.loadingText || this.getAttribute("data-loading-text");

		this.setLiveRegionText(this.loadingText);
		this.setAttribute("loading", true);
	}

	setLiveRegionText(statusText) {
		this.statusElement.setAttribute("aria-hidden", "false");
		this.statusElement.textContent = statusText;

		setTimeout(() => {
			this.statusElement.setAttribute("aria-hidden", "true");
		}, 1000);
	}

	renderSearchResults(resultsMarkup) {
		this.predictiveSearchResults.innerHTML = resultsMarkup;
		this.setAttribute("results", true);

		this.setLiveRegionResults();
		this.open();
		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}
		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}
	}

	clickSearchTabs() {
		const results = this.querySelectorAll(".predictive-search__results-list");
		if (results.length != 0) {
			results[0].classList.add("active");
			this.querySelector(".predictive-search__result-tab").classList.add(
				"active",
			);
			this.querySelectorAll(".predictive-search__result-tab").forEach((tab) => {
				tab.addEventListener("click", (event) => {
					event.preventDefault();
					const typeTarget = tab.dataset.typeTarget;
					this.querySelectorAll(".predictive-search__result-tab").forEach(
						(element) => {
							element.classList.remove("active");
						},
					);
					tab.classList.add("active");

					results.forEach((element) => {
						let resultsType = element.dataset.type;
						if (resultsType == typeTarget) {
							element.classList.add("active");
						} else {
							element.classList.remove("active");
						}
					});
				});
				tab.addEventListener("keydown", (event) => {
					if (event.key === "Enter") {
						event.preventDefault();
						const typeTarget = tab.dataset.typeTarget;
						this.querySelectorAll(".predictive-search__result-tab").forEach(
							(element) => {
								element.classList.remove("active");
							},
						);
						tab.classList.add("active");
			
						results.forEach((element) => {
							let resultsType = element.dataset.type;
							if (resultsType == typeTarget) {
								element.classList.add("active");
							} else {
								element.classList.remove("active");
							}
						});
					}
				});
			});
		}
	}

	hidePromoBlock() {
		if (this.classList.contains("search-modal__form")) {
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.add("hidden");
			}
		}
	}

	showPromoBlock() {
		if (this.classList.contains("search-modal__form")) {
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.remove("hidden");
			}
		}
	}

	setLiveRegionResults() {
		this.removeAttribute("loading");
		this.setLiveRegionText(
			this.querySelector("[data-predictive-search-live-region-count-value]")
				.textContent,
		);
	}

	initEventListeners() {
		document.querySelectorAll('.search-modal .search-reset__button').forEach((button) => {
			button.addEventListener('click', (event) => {
				event.preventDefault();
				this.close(true);
				this.updateSearchForTerm(this.input.value, "");
			});
		})
		document.querySelectorAll('.search-modal-mobile .search-reset__button').forEach((button) => {
			button.addEventListener('click', (event) => {			
				event.preventDefault();
				this.close(true);
				this.updateSearchForTerm(this.input.value, "");
			});
		})
	}

	open() {
		this.setAttribute("open", true);
		this.input.setAttribute("aria-expanded", true);
		this.isOpen = true;
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
		buttonHoverEffects();
		document.querySelectorAll('.search-modal__overlay').forEach((overlay) => {
			overlay.classList.remove('hide-overlay');
		})
	}

	close(clearSearchTerm = false) {
		this.closeResults(clearSearchTerm);
		this.isOpen = false;
	}

	closeResults(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = "";
			this.removeAttribute("results");
		}
		const selected = this.querySelector('[aria-selected="true"]');

		if (selected) selected.setAttribute("aria-selected", false);

		this.input.setAttribute("aria-activedescendant", "");
		this.removeAttribute("loading");
		this.removeAttribute("open");
		this.input.setAttribute("aria-expanded", false);
		this.predictiveSearchResults.removeAttribute("style");
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
		if (document.querySelector('.no-collection')) {
			document.querySelectorAll('.search-modal__overlay').forEach((overlay) => {
				overlay.classList.add('hide-overlay')
			})
			document.documentElement.classList.remove('overflow-hidden');
				document.querySelector('body').classList.remove('overflow-hidden');
		}
	}
}

customElements.define("predictive-search", PredictiveSearch);
